import React, { useEffect, useRef, useState } from "react";
import EndpointsEnum from "../../../models/EndpointsEnum";
import FileUploadResourceTypeEnum from "../../../models/FileUploadResourceTypeEnum";
import IAttachment from "../../../models/IAttachment";
import IDeleteFileUploadDTO from "../../../models/DTO/IDeleteFileUploadDTO";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IGetAttachmentSignedUrlDTO from "../../../models/DTO/IGetAttachmentSignedUrlDTO";
import IOnGetAttachmentSignedUrlDTO from "../../../models/DTO/IOnGetAttachmentSignedUrlDTO";
import IOrder from "../../../models/IOrder";
import ISupplierModel from "../../../models/ISupplierModel";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IUserUI from "../../../models/IUserUI";
import UserTypesEnum from "../../../models/UserTypesEnum";
import { useAppContextNonNull } from "@contexts/AppContext";
import { OrdersService } from "@components/orders/Orders/OrdersService";
import OrderEntryService from "../../../services/OrderEntryService";
import UserService from "../../../services/UserService";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwS3Uploader from "../../shared/OwS3Uploader/OwS3Uploader";
import "./EcommerceOrderFiles.scss";

type Props = {
    order: IOrder;
    /** Called once an upload or delete has landed on the API, so the owner reloads the order. */
    onFilesChanged: () => void;
};

/**
 * Whether this user may add files to (and remove their own files from) the order on the eCommerce
 * order details page: the order's workflow step still lets them change the order, or the step
 * explicitly keeps file uploads open while the order is read only. Cancelled orders stay closed.
 */
export const canEditOrderFiles = (order: IOrder, userModel: IUserUI, supplierModel: ISupplierModel): boolean => {
    if (order.is_cancelled) {
        return false;
    }

    const readOnly = OrdersService.isReadOnly({
        order,
        isOfflineOrder: false,
        hasEditMode: true,
        userModel,
        supplierModel,
        section: "DETAILS_PANEL",
    });

    if (!readOnly) {
        return true;
    }

    const currentStep = OrderEntryService.getCurrentOrderStep(order, supplierModel);

    return !!currentStep?.allowFileUploadIfReadOnly;
};

/**
 * Whether the user may delete files other people uploaded. Supplier admins may remove any file on the
 * order; everyone else only their own uploads.
 */
export const canDeleteAnyOrderFile = (userModel: IUserUI): boolean => {
    return !!userModel.isSupplierUser && userModel.userType?.id === UserTypesEnum.ADMIN;
};

/** The files attached to the order itself (item images live on the items). Same precedence as the orders list badge. */
export const getOrderFiles = (order: IOrder): IAttachment[] => {
    const attachments = order.Attachments || order.attachments || [];

    return attachments.filter((attachment) => !attachment.image_seq);
};

const getFileIconClass = (name?: string): string => {
    const extension = (name || "").split(".").pop()?.toLowerCase() || "";

    switch (extension) {
        case "pdf":
            return "fad fa-file-pdf";
        case "doc":
        case "docx":
            return "fad fa-file-word";
        case "xls":
        case "xlsx":
        case "csv":
            return "fad fa-file-excel";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
        case "bmp":
        case "svg":
            return "fad fa-file-image";
        case "zip":
            return "fad fa-file-archive";
        default:
            return "fad fa-file-alt";
    }
};

/** How long a delete may go unanswered before its controls are handed back so the user can retry. */
export const DELETE_TIMEOUT_MS = 15000;

/** The API's notify messages for a delete that did not go through. */
const DELETE_ERROR_MESSAGES = ["ERROR_DELETE_FILE_UPLOAD", "NO_ACCESS_TO_DELETE_FILE_UPLOAD"];

export const formatFileSize = (size?: number): string => {
    if (!size) {
        return "0 KB";
    }

    if (size >= 1000000) {
        return `${(size / 1000000).toFixed(2)} MB`;
    }

    return `${(size / 1000).toFixed(size >= 100000 ? 0 : 1)} KB`;
};

/**
 * The Files card on the eCommerce order details page: every file attached to the order with a
 * download, an upload zone while the order can still be changed, and a delete on the files the
 * signed-in user uploaded themselves, or on every file for supplier admins (the API allows supplier
 * users to delete any of the supplier's files and account users only their own).
 */
export const EcommerceOrderFiles: React.FC<Props> = (props) => {
    const { userModel, supplierModel, vendorModel, socket } = useAppContextNonNull();

    const [pendingDeleteGuid, setPendingDeleteGuid] = useState<string | null>(null);
    const [deletingGuid, setDeletingGuid] = useState<string | null>(null);
    const deleteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const files = getOrderFiles(props.order);
    const canEdit = canEditOrderFiles(props.order, userModel, supplierModel);
    const canDeleteAny = canDeleteAnyOrderFile(userModel);
    const uploaderVendorModel = userModel.isSupplierUser ? undefined : vendorModel;

    const onGetAttachmentSignedUrl = (data: IOnGetAttachmentSignedUrlDTO) => {
        if (!files.some((file) => file.guid === data.attachmentGuid)) {
            return;
        }

        const file = files.find((attachment) => attachment.guid === data.attachmentGuid)!;
        const link = document.createElement("a");

        link.href = data.signedUrl;
        link.download = file.name;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clearDeleteTimer = () => {
        if (deleteTimer.current) {
            clearTimeout(deleteTimer.current);
            deleteTimer.current = null;
        }
    };

    /** Hands the delete controls back, whether the delete went through, failed, or never answered. */
    const settleDelete = () => {
        clearDeleteTimer();
        setDeletingGuid(null);
        setPendingDeleteGuid(null);
    };

    const onDeleteFileUpload = () => {
        settleDelete();
        props.onFilesChanged();
    };

    // The API answers a refused or failed delete with a notify error (the app shows the toast); the
    // file list stays as the server has it, so reload it and let the user try again.
    const onNotify = (data: IErrorDTO) => {
        if (deletingGuid && DELETE_ERROR_MESSAGES.includes(data?.message)) {
            settleDelete();
            props.onFilesChanged();
        }
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.FileUploadOnGetAttachmentSignedUrl, fn: onGetAttachmentSignedUrl },
        { endpoint: EndpointsEnum.FileUploadOnDeleteFileUpload, fn: onDeleteFileUpload },
        { endpoint: EndpointsEnum.Notify, fn: onNotify },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, [props.order, deletingGuid]);

    useEffect(() => clearDeleteTimer, []);

    const downloadFile = (attachment: IAttachment) => {
        // Account users (including a supplier user switched into an account) only get access through the
        // vendor, so the request has to carry it, as the upload and delete requests do.
        const payload: IGetAttachmentSignedUrlDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: uploaderVendorModel ? uploaderVendorModel.guid : undefined,
            attachmentGuid: attachment.guid,
        };

        socket.emit(EndpointsEnum.FileUploadGetAttachmentSignedUrl, payload);
    };

    const deleteFile = (attachment: IAttachment) => {
        const payload: IDeleteFileUploadDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            attachmentGuid: attachment.guid,
            vendorGuid: uploaderVendorModel ? uploaderVendorModel.guid : undefined,
        };

        clearDeleteTimer();
        setDeletingGuid(attachment.guid);
        deleteTimer.current = setTimeout(settleDelete, DELETE_TIMEOUT_MS);
        socket.emit(EndpointsEnum.FileUploadDeleteFileUpload, payload);
    };

    const isOwnFile = (attachment: IAttachment) => attachment.user_id === userModel.id;

    return (
        <div className={"EcommerceOrderFiles"} data-testid={"ecommerce-order-files"}>
            <div className={"order-files-header"}>
                <OwLabel title={"files"} className={"order-details-section-label"} noBottomMargin={true} />
                <span className={"order-details-count"}>{files.length}</span>
            </div>

            {files.length ? (
                <ul className={"order-files-list"}>
                    {files.map((file) => {
                        const own = isOwnFile(file);
                        const confirming = pendingDeleteGuid === file.guid;
                        const deleting = deletingGuid === file.guid;

                        return (
                            <li className={"order-file"} key={file.guid} data-testid={`order-file-${file.guid}`}>
                                <i className={`order-file-icon ${getFileIconClass(file.name)}`} aria-hidden={true} />

                                <div className={"order-file-info"}>
                                    <button type={"button"} className={"order-file-name"} onClick={() => downloadFile(file)}>
                                        {file.name}
                                    </button>
                                    <div className={"order-file-meta"}>
                                        <span>{formatFileSize(file.size)}</span>
                                        {file.created_at ? <span>{UserService.parseDate(file.created_at)}</span> : null}
                                        {own ? (
                                            <span className={"order-file-own"}>
                                                <OwLabel title={"uploaded_by_you"} noBottomMargin={true} />
                                            </span>
                                        ) : null}
                                    </div>
                                </div>

                                <div className={"order-file-actions"}>
                                    {confirming ? (
                                        <>
                                            <button
                                                type={"button"}
                                                className={"order-file-btn is-danger"}
                                                disabled={deleting}
                                                onClick={() => deleteFile(file)}
                                            >
                                                {deleting ? (
                                                    <i className={"fas fa-circle-notch fa-spin"} aria-hidden={true} />
                                                ) : (
                                                    <i className={"fas fa-trash-alt"} aria-hidden={true} />
                                                )}
                                                <OwLabel title={"confirm_delete"} noBottomMargin={true} />
                                            </button>
                                            <button
                                                type={"button"}
                                                className={"order-file-btn"}
                                                disabled={deleting}
                                                onClick={() => setPendingDeleteGuid(null)}
                                            >
                                                <OwLabel title={"cancel"} noBottomMargin={true} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type={"button"}
                                                className={"order-file-btn"}
                                                title={"download"}
                                                aria-label={`download ${file.name}`}
                                                onClick={() => downloadFile(file)}
                                            >
                                                <i className={"fas fa-download"} aria-hidden={true} />
                                                <OwLabel title={"download"} noBottomMargin={true} />
                                            </button>
                                            {canEdit && (own || canDeleteAny) ? (
                                                <button
                                                    type={"button"}
                                                    className={"order-file-btn is-danger"}
                                                    title={"delete"}
                                                    aria-label={`delete ${file.name}`}
                                                    onClick={() => setPendingDeleteGuid(file.guid)}
                                                >
                                                    <i className={"fas fa-trash-alt"} aria-hidden={true} />
                                                    <OwLabel title={"delete"} noBottomMargin={true} />
                                                </button>
                                            ) : null}
                                        </>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <div className={"order-files-empty"}>
                    <OwLabel title={"order_files_empty"} noBottomMargin={true} />
                </div>
            )}

            {canEdit ? (
                <div className={"order-files-upload"}>
                    <OwS3Uploader
                        socket={socket}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        vendorModel={uploaderVendorModel}
                        orderGuid={props.order.guid}
                        isReadOnly={false}
                        resourceType={FileUploadResourceTypeEnum.orderItem}
                        compact={true}
                        onUploaded={props.onFilesChanged}
                        testId={"ecommerce-order-files-uploader"}
                    />
                </div>
            ) : (
                <div className={"order-files-read-only"}>
                    <i className={"fad fa-lock"} aria-hidden={true} />
                    <OwLabel title={"order_files_read_only"} noBottomMargin={true} />
                </div>
            )}
        </div>
    );
};

export default EcommerceOrderFiles;
